<script setup>
import {TypeOfActivityList} from '../../constants/TypeOfActivity';
import {TypeOfActivityConst} from '../../constants/TypeOfActivity';
import {defineModel, ref, defineProps, defineEmits} from 'vue';
import {required} from '@ohrm/core/util/validation/rules';

const props = defineProps({
  informationsList: {
    type: Array,
    default() {
      return [];
    },
  },
  trainingsList: {
    type: Array,
    default() {
      return [];
    },
  },
  tasksList: {
    type: Array,
    default() {
      return [];
    },
  },
  responsibles: {
    type: Array,
    default() {
      return [];
    },
  },
  isEdit: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emit = defineEmits([
  'showInformationModal',
  'showTrainingModal',
  'showTaskModal',
]);

const stages = defineModel('stages');
const deletedStages = defineModel('deletedStages');
const deletedStageActivities = defineModel('deletedStageActivities');

const rules = ref({
  typeOfActivities: [required],
  responsibles: [required],
});

const addActivityToStage = index => {
  if (stages.value[index]['infos'] == undefined) {
    stages.value[index]['infos'] = [];
  }
  stages.value[index]['infos'].push({
    typeOfActivity: null,
    activity: null,
    responsible: null,
    dueDate: null,
  });
};

const addStage = () => {
  stages.value.push({infos: []});
  addActivityToStage(stages.value.length - 1);
};

const removeStage = index => {
  if (stages.value[index].id != undefined)
    deletedStages.value.push(stages.value[index].id);
  stages.value.splice(index, 1);
};

const stageActivityOptions = typeOfActivityOfStage => {
  switch (typeOfActivityOfStage) {
    case TypeOfActivityConst.INFORMATION:
      return props.informationsList;
    case TypeOfActivityConst.TRAINING:
      return props.trainingsList;
    case TypeOfActivityConst.TASK:
      return props.tasksList;
    default:
      return [];
  }
};

const stageOpenModal = (
  typeOfActivityOfStage,
  stageIndex,
  stageActivityIndex,
) => {
  switch (typeOfActivityOfStage) {
    case TypeOfActivityConst.INFORMATION:
      emit('showInformationModal', stageIndex, stageActivityIndex);
      break;
    case TypeOfActivityConst.TRAINING:
      emit('showTrainingModal', stageIndex, stageActivityIndex);
      break;
    case TypeOfActivityConst.TASK:
      emit('showTaskModal', stageIndex, stageActivityIndex);
      break;
  }
};

const clearStageActivity = (stageIndex, activityIndex) => {
  stages.value[stageIndex][activityIndex].activity = null;
};

const removeActivityToStage = (stageIndex, activityIndex) => {
  if (stages.value[stageIndex]['infos'][activityIndex]?.id != undefined)
    deletedStageActivities.value.push(
      stages.value[stageIndex]['infos'][activityIndex].id,
    );

  stages.value[stageIndex]['infos'].splice(activityIndex, 1);
};
</script>
<template>
  <!-- PLAN OF ACTIVITIES -->
  <div>
    <oxd-grid :cols="8" class="orangehrm-full-width-grid">
      <template v-for="(stage, index) in stages" :key="index">
        <oxd-grid-item class="--span-column-7" style="margin-bottom:18px;">
          <oxd-table-filter
            :filter-title="`Stage #${index + 1}`"
            :is-active="false"
            style="background: #F5F5F5;"
          >
            <oxd-form-row
              v-for="(stageData, activityIndex) in stage?.infos"
              :key="activityIndex"
            >
              <oxd-grid :cols="6" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="stageData.typeOfActivity"
                    style="background:white"
                    label="Type of Activity"
                    value="Select"
                    type="select"
                    :options="TypeOfActivityList"
                    :rules="rules.typeOfActivities"
                    required
                    @update:modelValue="
                      clearStageActivity(index, activityIndex)
                    "
                  />
                </oxd-grid-item>
                <oxd-grid-item class="--span-column-2 flex items-center">
                  <oxd-input-field
                    v-model="stageData.activity"
                    style="background:white;"
                    label="Activity"
                    value="Select"
                    type="select"
                    :options="
                      stageActivityOptions(stageData.typeOfActivity?.id)
                    "
                    required
                  />
                  <oxd-button
                    :disabled="
                      stageData.activity?.id || !stageData.typeOfActivity?.id
                    "
                    style="margin-top: 10px; width:130px; padding: 8px"
                    label="New"
                    icon-name="plus"
                    display-type="text"
                    @click="
                      stageOpenModal(
                        stageData.typeOfActivity?.id,
                        index,
                        activityIndex,
                      )
                    "
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="stageData.responsible"
                    style="background:white"
                    label="Manager"
                    value="Select"
                    type="select"
                    :options="responsibles"
                    :rules="rules.responsible"
                    required
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <date-input v-model="stageData.dueDate" label="Due Date" />
                </oxd-grid-item>

                <oxd-grid-item class="flex items-center">
                  <oxd-icon-button
                    :disabled="stage?.infos.length <= 1"
                    name="trash"
                    class="oxd-button--secondary"
                    @click="removeActivityToStage(index, activityIndex)"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </oxd-form-row>

            <oxd-icon-button
              name="plus"
              class="oxd-button--secondary"
              @click="addActivityToStage(index)"
            />
          </oxd-table-filter>
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-icon-button
            style="margin-top:20px"
            name="trash"
            class="oxd-button--secondary"
            @click="removeStage(index)"
          />
        </oxd-grid-item>
      </template>
    </oxd-grid>
    <oxd-button
      style="margin-top: 10px"
      label="Stage"
      icon-name="plus"
      display-type="text"
      @click="addStage"
    />
  </div>
  <!-- END: PLAN OF ACTIVITIES -->
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-20 {
  width: 20%;
}

.flex {
  display: flex;
}

.gap-3 {
  gap: 3px;
}

.items-center {
  align-items: center;
}
</style>
